// lib/canvas-modes.js — CANVAS MODE REGISTRY
// ═══════════════════════════════════════════════════════════
// Single source of truth for which artifact kinds have an editor
// and which component renders that editor. The registry is the
// extension point: adding a new mode is one entry.
//
// Each entry:
//   {
//     kind: string         — matches the kind returned by
//                            classifyArtifact() in workstation-canvas-panel
//     label: string        — short display name in the header pill
//     editable: boolean    — does this kind have an editor?
//     editor: Component    — lazy-imported editor component (only loads
//                            when the user actually opens it for editing)
//   }
//
// Read views all live in components/canvas-artifact-preview.js — the
// registry only owns the EDITOR side. Read mode is universal.
// ═══════════════════════════════════════════════════════════

import dynamic from 'next/dynamic';

// Lazy-loaded so the editor bundles only ship when the user actually
// opens an artifact for editing.
const DocEditor = dynamic(
  () => import('../components/canvas-doc-editor'),
  { ssr: false, loading: () => null }
);
const CodeEditor = dynamic(
  () => import('../components/canvas-code-editor'),
  { ssr: false, loading: () => null }
);
const HtmlEditor = dynamic(
  () => import('../components/canvas/canvas-html-editor'),
  { ssr: false, loading: () => null }
);

// The registry. Add new modes here as their editors land.
export const CANVAS_MODES = {
  doc: {
    kind: 'doc',
    label: 'Doc',
    editable: true,
    editor: DocEditor,
  },
  code: {
    kind: 'code',
    label: 'Code',
    editable: true,
    editor: CodeEditor,
  },
  html: {
    kind: 'html',
    label: 'Page',
    editable: true,
    editor: HtmlEditor,
  },
  svg:     { kind: 'svg',     label: 'SVG',      editable: false },
  image:   { kind: 'image',   label: 'Image',    editable: false },
  link:    { kind: 'link',    label: 'Link',     editable: false },
  unknown: { kind: 'unknown', label: 'Artifact', editable: false },
};

// Convenience accessor — never throw if the kind is unknown.
export function getMode(kind) {
  return CANVAS_MODES[kind] || CANVAS_MODES.unknown;
}

// Is this kind editable? Used by the preview overlay to decide
// whether to render the Read/Edit toggle.
export function isEditable(kind) {
  return !!CANVAS_MODES[kind]?.editable;
}
